import type { ViteDevServer, PluginOption } from 'vite';
import { dirname, isAbsolute, join } from 'node:path';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getViteServerUrl } from '../vite-server-url.js';

const originPlaceholder = '__WIX_ORIGIN_PLACEHOLDER__';

function getAbsoluteAssetUrl(
  originalUrl: string,
  assetUrl: string,
  viteAddress: string | undefined
) {
  const url = new URL(assetUrl, viteAddress).href;

  if (assetUrl.startsWith('/@vite')) {
    return url;
  }

  if (isAbsolute(assetUrl)) {
    return url;
  }

  return new URL(join(dirname(originalUrl), assetUrl), viteAddress).href;
}

export function autoOrigin(): PluginOption {
  let viteServer: ViteDevServer;
  let origin: string | undefined;

  return {
    name: 'wix:auto-origin',
    apply: 'serve',
    config: (config) => {
      if (config.server?.origin) {
        origin = config.server.origin;
        return;
      }

      return {
        server: {
          origin: originPlaceholder,
        },
      };
    },
    configureServer: (server: ViteDevServer) => {
      viteServer = server;

      server.middlewares.use((_req, _res, next) => {
        origin ??= getViteServerUrl(server).replace(/\/$/, '');
        next();
      });
    },
    transform: (code, id) => {
      if (/.*\.(m?jsx?|tsx?)$/.test(id)) {
        return code;
      }

      if (!origin) {
        throw new CliError({
          code: CliErrorCode.FailedToResolveViteOrigin(),
          cause: null,
        });
      }

      return code.replaceAll(originPlaceholder, origin);
    },
    // Similar problem reported in vite repo: https://github.com/vitejs/vite/issues/14263
    // Clarification:
    // Embedded scripts will be added to the user's website.
    // If it has a relative URL, for example: `<script type="module" src="./logger.ts"></script>`,
    // the browser will attempt to load the script from the website's domain,
    // resulting in a URL like this: `https://artemdem7.wixsite.com/logger.ts`, which is unavailable.
    // To make it work, we need to use an absolute URL to load the script from the localhost.
    transformIndexHtml: {
      order: 'post',
      handler(html, { originalUrl }) {
        // Since transformIndexHtml can be called before the middleware we need to set it here
        origin ??= getViteServerUrl(viteServer).replace(/\/$/, '');

        if (!originalUrl) {
          return;
        }

        const regexReplace = (
          _: string,
          prefix: string,
          assetUrl: string,
          suffix: string
        ) => {
          const url = getAbsoluteAssetUrl(originalUrl, assetUrl, origin);

          return `${prefix}${url}${suffix}`;
        };

        return html
          .replaceAll(
            /(<script [^><]*src=["'])((?!http)[^"']+)(["'][^><]*>[\r\n]*<\/script>)/g,
            regexReplace
          )
          .replaceAll(
            /(<link [^><]*href=["'])((?!http)[^"']+)(["'][^><]*>)/g,
            regexReplace
          )
          .replaceAll(/(\r\n|\n|\r)/gm, '');
      },
    },
  };
}
